import { NEW_LOOK_RADIUS } from "common/NewLook/tokens.ts";
import { NewPaymentPriceResponse, SubscriptionDuration } from "@shared/types";
import { BuySubscriptionContext } from "features/settings/Subscription/BuySubscription.context";
import React, { useContext } from "react";
import styled, { css, useTheme } from "styled-components";
import { formatMoney } from "features/settings/Subscription/NewLook/formatMoney.ts";
import { hexToRGB } from "@shared/utils/misc";
import Radio from "common/Radio/Radio";
import Flex from "common/Boxes/Flex";
import {
  DURATION_TO_PRIMARY_DISPLAY,
  DURATION_TO_SECONDARY_DISPLAY,
} from "features/settings/common/types";
import { InitialPrice } from "features/settings/common/styles";
import {
  convertPriceToMonthlyPrice,
  getInitialPrice,
  getInitialTotalPrice,
  getPlanDiscount,
} from "@shared/features/settings/subscription/utils";
import Text from "common/Typography/Text";
import { typography } from "@shared/common/Themes/typography";
import useMedia from "utils/hooks/useMedia.ts";
import { MOBILE_SEARCH } from "common/constants.ts";
import { SMALL_MOBILE_CHECKOUT_HEIGHT } from "features/settings/common/constants.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { round } from "@shared/utils/numbers.ts";
import { phoneMaxWidth } from "common/NewLook/layout.ts";

interface DurationRadioProps {
  duration: SubscriptionDuration;
  promoCode?: NewPaymentPriceResponse;
  hideRadio?: boolean;
}

const DurationRadio = ({
  duration,
  promoCode,
  hideRadio,
}: DurationRadioProps) => {
  const {
    plan,
    plans,
    duration: selectedDuration,
    setDuration,
    isBottomSummary,
  } = useContext(BuySubscriptionContext)!;
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const { isNewLook } = useTheme();
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const [_, discountPercent, yearlyDiscountAmount] =
    plan?.subscription_plan_type
      ? getPlanDiscount(
          plans,
          plan.subscription_plan_type,
          duration,
          promoCode?.recurring_price
        )
      : [];

  // The new look always shows cents on fractional prices.
  const money = (value?: number | string) =>
    isNewLook ? formatMoney(value) : value;

  let description = t("duration_radio.base_price");
  if (duration !== 30) {
    description = t("duration_radio.save_compared_monthly", {
      amount: money(yearlyDiscountAmount),
    });
  } else if (yearlyDiscountAmount) {
    description = t("duration_radio.save_yearly_compared_regular", {
      amount: money(yearlyDiscountAmount),
    });
  }

  const planDurationInfo = plan?.[duration];

  const price =
    promoCode?.recurring_price ||
    round(
      (planDurationInfo?.price || 0) - (planDurationInfo?.discount || 0),
      2
    );

  const monthlyPrice = convertPriceToMonthlyPrice(price, duration);
  const initialMonthlyPrice = getInitialPrice(plan, 30);
  const showInitialPrice = initialMonthlyPrice !== monthlyPrice;
  const checked = selectedDuration === duration;
  const monthlyPriceColorType = checked ? "primary" : "secondary";
  const annuallyPriceColorType = checked ? "medium" : "secondary";

  const durationTitle = (
    <div>
      <Flex $justifyContent="flex-start">
        <PrimaryText>{t(DURATION_TO_SECONDARY_DISPLAY[duration])}</PrimaryText>
        {!!discountPercent && (
          <Tag>
            {t("duration_radio.save_tag", { percent: discountPercent })}
          </Tag>
        )}
      </Flex>
      <Text type="medium" $size={isMobile ? "S" : undefined}>
        {description}
      </Text>
    </div>
  );

  const displayedMonthlyPrice = (
    <Flex>
      {showInitialPrice && (
        <InitialPrice type={monthlyPriceColorType}>
          ${money(initialMonthlyPrice)}
        </InitialPrice>
      )}
      <MonthlyPrice type={monthlyPriceColorType}>
        ${money(monthlyPrice)} {t("duration_radio.per_month")}
      </MonthlyPrice>
    </Flex>
  );

  const displayedAnnuallyPrice = duration !== 30 && (
    <Flex>
      <InitialPrice type={annuallyPriceColorType}>
        ${money(getInitialTotalPrice(plan, duration))}
      </InitialPrice>
      <Text type={annuallyPriceColorType}>
        ${money(price)} /{" "}
        {t(DURATION_TO_PRIMARY_DISPLAY[duration]).toLowerCase()}
      </Text>
    </Flex>
  );

  return (
    <Wrapper
      $checked={checked}
      onClick={() => setDuration(duration)}
      style={isBottomSummary ? { paddingBottom: 0 } : undefined}
    >
      {!hideRadio && (
        <Radio checked={checked} $size={isBottomSummary ? 18 : 20} />
      )}
      <DurationWrapper style={isBottomSummary ? { paddingLeft: 0 } : undefined}>
        {durationTitle}
        <PricesWrapper>
          {displayedMonthlyPrice}
          {displayedAnnuallyPrice}
        </PricesWrapper>
      </DurationWrapper>
    </Wrapper>
  );
};

export default DurationRadio;

const Wrapper = styled.div<{ $checked?: boolean }>`
  width: 100%;
  padding: 12px 0;
  cursor: pointer;
  display: flex;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      && {
        align-items: center;
        margin-top: 12px;
        padding: 12px 16px;
        border-radius: 12px;
        border: ${p.theme.borders.card};
        background: ${p.theme.colors.cardBgColor};
        transition: border-color 0.15s ease, background 0.15s ease;
      }
      &:hover {
        border-color: ${p.theme.colors.secondaryText};
      }
      /* Phone: the prices stack under the text, so the radio follows the
         option name instead of the middle of the tile. */
      @media (max-width: 500px) {
        && {
          align-items: flex-start;
        }
      }
      ${p.$checked &&
      css`
        &&,
        &&:hover {
          border-color: ${p.theme.colors.primaryBlue};
          background: ${hexToRGB(p.theme.colors.primaryBlue, 0.04)};
          box-shadow: 0 0 0 1px ${p.theme.colors.primaryBlue};
        }
      `}
    `}
`;
const PricesWrapper = styled.div`
  padding-left: 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  flex: 0 0 auto;
  @media (max-width: ${phoneMaxWidth}) {
    padding-left: 8px;
  }
`;
const DurationWrapper = styled.div`
  padding-left: 8px;
  flex: auto;
  display: flex;
  justify-content: space-between;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      padding-left: 12px;
      align-items: center;
      font-variant-numeric: tabular-nums;
      .ant-typography {
        line-height: 1.45;
      }
      /* Phone: the prices sit under the description instead of beside it. */
      @media (max-width: 500px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 4px;
        ${PricesWrapper} {
          align-items: flex-start;
          padding-left: 0;
          > div {
            justify-content: flex-start;
          }
        }
      }
    `}
`;
const PrimaryText = styled(Text).attrs({
  $size: typography.fontSizes.L,
  $weight: typography.fontWeights.SEMI_BOLD,
})`
  @media (max-height: ${SMALL_MOBILE_CHECKOUT_HEIGHT}) {
    font-size: ${(p) => p.theme.typography.fontSizes.M};
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      /* The option's name in the body size; its price as a figure. */
      && {
        font-size: 14px;
        line-height: 20px;
      }
    `}
`;
/** In the new look the price is a value (500), not a title. */
const MonthlyPrice = styled(PrimaryText)`
  ${(p) =>
    p.theme.isNewLook &&
    css`
      && {
        font-size: 15px;
        line-height: 20px;
        font-weight: ${p.theme.typography.fontWeights.MEDIUM};
        font-variant-numeric: tabular-nums;
      }
    `}
`;
export const Tag = styled.div`
  background: ${(p) => p.theme.colors.primaryBlue};
  color: ${(p) => p.theme.colors.buttonText};
  white-space: nowrap;
  margin-left: 6px;
  font-size: ${(p) => p.theme.typography.fontSizes.S};
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  padding: 3px 4px;
  line-height: 14px;
  border-radius: 4px;
  @media (max-width: ${phoneMaxWidth}) {
    font-size: 11px;
    padding-bottom: 2px;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      && {
        margin-left: 8px;
        padding: 1px 8px;
        border-radius: ${NEW_LOOK_RADIUS.chip}px;
        background: ${hexToRGB(p.theme.colors.green, 0.12)};
        color: ${p.theme.colors.green};
        font-size: 11.5px;
        line-height: 16px;
        font-weight: ${p.theme.typography.fontWeights.MEDIUM};
      }
    `}
`;
